import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthCallback } from './components/AuthCallback';
import { Toaster } from './components/ui/sonner';
import { InstallPrompt } from './components/InstallPrompt';
import { SplashGate } from './components/SplashScreen';
import { PhoneOnboardingModal } from './components/PhoneOnboardingModal';
import { TopProgressBar } from './components/TopProgressBar';
import { TermsPopup } from './components/TermsPopup';
import { PageLoader } from './components/PageLoader';
import { initCapacitor } from './utils/capacitor';
import '@/App.css';

// Initialize Capacitor (native plugins)
initCapacitor();

// ==================== LAZY-LOADED PAGES (Code Splitting) ====================
// Critical path - loaded eagerly
import { LoginPage } from './pages/LoginPage';

// Everything else lazy-loaded for faster initial bundle
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const CommandCenterDashboard = React.lazy(() => import('./pages/CommandCenterDashboard').then(m => ({ default: m.CommandCenterDashboard })));
const AddPropertyPage = React.lazy(() => import('./pages/AddPropertyPage').then(m => ({ default: m.AddPropertyPage })));
const PropertiesPage = React.lazy(() => import('./pages/PropertiesPage').then(m => ({ default: m.PropertiesPage })));
const AgentsPage = React.lazy(() => import('./pages/AgentsPage').then(m => ({ default: m.AgentsPage })));
const TownPlanningPage = React.lazy(() => import('./pages/TownPlanningPage').then(m => ({ default: m.TownPlanningPage })));
const GuideMapsPage = React.lazy(() => import('./pages/GuideMapsPage').then(m => ({ default: m.GuideMapsPage })));
const DetailedPrintPage = React.lazy(() => import('./pages/DetailedPrintPage').then(m => ({ default: m.DetailedPrintPage })));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const WalletPage = React.lazy(() => import('./pages/WalletPage').then(m => ({ default: m.WalletPage })));
const MarketplacePage = React.lazy(() => import('./pages/MarketplacePage').then(m => ({ default: m.MarketplacePage })));
const MarketplaceViewPage = React.lazy(() => import('./pages/MarketplaceViewPage').then(m => ({ default: m.MarketplaceViewPage })));
const MyListingsPage = React.lazy(() => import('./pages/MyListingsPage').then(m => ({ default: m.MyListingsPage })));
const ChatWithAIPage = React.lazy(() => import('./pages/ChatWithAIPage').then(m => ({ default: m.ChatWithAIPage })));
const FormBuilderPage = React.lazy(() => import('./pages/FormBuilderPage').then(m => ({ default: m.FormBuilderPage })));
const AdminPanelPage = React.lazy(() => import('./pages/AdminPanelPage').then(m => ({ default: m.AdminPanelPage })));
const AdminPresetsPage = React.lazy(() => import('./pages/AdminPresetsPage').then(m => ({ default: m.AdminPresetsPage })));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const SupportPage = React.lazy(() => import('./pages/SupportPage').then(m => ({ default: m.SupportPage })));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const PublicPropertyViewPage = React.lazy(() => import('./pages/PublicPropertyViewPage').then(m => ({ default: m.PublicPropertyViewPage })));

const HealthDashboardPage = React.lazy(() => import('./pages/HealthDashboardPage'));
const AgentAnalyticsPage = React.lazy(() => import('./pages/AgentAnalyticsPage').then(m => ({ default: m.AgentAnalyticsPage })));
const AuditLogsPage = React.lazy(() => import('./pages/AuditLogsPage').then(m => ({ default: m.AuditLogsPage })));
const ShareTemplatesPage = React.lazy(() => import('./pages/ShareTemplatesPage').then(m => ({ default: m.ShareTemplatesPage })));
const MapSearchPage = React.lazy(() => import('./pages/MapSearchPage'));
const SoldPropertiesCRMPage = React.lazy(() => import('./pages/SoldPropertiesCRMPage').then(m => ({ default: m.SoldPropertiesCRMPage })));
const TrialManagementPage = React.lazy(() => import('./pages/TrialManagementPage').then(m => ({ default: m.TrialManagementPage })));
const ClientRequirementsPage = React.lazy(() => import('./pages/ClientRequirementsPage').then(m => ({ default: m.ClientRequirementsPage })));
const PushNotificationsPage = React.lazy(() => import('./pages/PushNotificationsPage').then(m => ({ default: m.PushNotificationsPage })));

// PageLoader imported from ./components/PageLoader

// Synchronous check for OAuth callback - detects session_id BEFORE routes render
const AppRouter = () => {
  const location = useLocation();
  // Check URL fragment for session_id or path for standard OAuth callback
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }
  return <AppRoutes />;
};

const AppRoutes = () => (
  <Routes>
                {/* Public Routes (no auth required) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/marketplace" element={<MarketplacePage />} />
                <Route path="/map-search" element={<MapSearchPage />} />
                <Route path="/property/:propertyId" element={<PublicPropertyViewPage />} />
                <Route path="/listing/:propertyId" element={<PublicPropertyViewPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <CommandCenterDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard-classic"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/add"
            element={
              <ProtectedRoute requireSubscription>
                <AddPropertyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties"
            element={
              <ProtectedRoute requireSubscription>
                <PropertiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sold-properties"
            element={
              <ProtectedRoute requireSubscription>
                <SoldPropertiesCRMPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/town-planning"
            element={
              <ProtectedRoute requireSubscription>
                <TownPlanningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guide-maps"
            element={
              <ProtectedRoute requireSubscription>
                <GuideMapsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/detailed-print"
            element={
              <ProtectedRoute requireSubscription>
                <DetailedPrintPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <WalletPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-listings"
            element={
              <ProtectedRoute requireSubscription>
                <MyListingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace-view"
            element={
              <ProtectedRoute requireSubscription>
                <MarketplaceViewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents"
            element={<Navigate to="/admin-panel" replace />}
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat-with-ai"
            element={
              <ProtectedRoute requireSubscription>
                <ChatWithAIPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <SubscriptionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={<Navigate to="/my-listings" replace />}
          />
          <Route
            path="/form-builder"
            element={
              <ProtectedRoute adminOnly>
                <FormBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-panel"
            element={
              <ProtectedRoute adminOnly>
                <AdminPanelPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-presets"
            element={
              <ProtectedRoute adminOnly>
                <AdminPresetsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/health"
            element={
              <ProtectedRoute adminOnly>
                <HealthDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent-analytics"
            element={
              <ProtectedRoute adminOnly>
                <AgentAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute adminOnly>
                <AuditLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/share-templates"
            element={
              <ProtectedRoute adminOnly>
                <ShareTemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trial-management"
            element={
              <ProtectedRoute adminOnly>
                <TrialManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-requirements"
            element={
              <ProtectedRoute requireSubscription>
                <ClientRequirementsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/push-notifications"
            element={
              <ProtectedRoute adminOnly>
                <PushNotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
);

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <TopProgressBar />
            <TermsPopup />
            <Suspense fallback={<PageLoader />}>
              <AppRouter />
            </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <InstallPrompt />
        <SplashGate />
        <PhoneOnboardingModal />
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
